import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type {
  CategoryEntity,
  CategoryGroupEntity,
  NoteEntity,
} from '@actual-app/core/types/models';
import { css } from '@emotion/css';

import { useBudgetActions } from '#budget';
import type { ApplyBudgetActionPayload } from '#budget';
import { prewarmMonth } from '#components/budget/util';
import { Checkbox } from '#components/forms';
import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import {
  hayFantasmaGuardar,
  suscribirFantasmaGuardar,
} from '#components/mobile/ayuda/escenas';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasAsignar } from '#components/mobile/ayuda/usePreguntasAsignar';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { useOpcionesPantalla } from '#components/mobile/opciones/OpcionesPantalla';
import { comprobarPlan } from '#components/mobile/plan/cerrojoPlan';
import { BarraProgreso } from '#components/mobile/ui/BarraProgreso';
import { Boton } from '#components/mobile/ui/Boton';
import { BotonRedondo } from '#components/mobile/ui/Cabecera';
import { Cargando } from '#components/mobile/ui/Cargando';
import { ConfirmarHoja } from '#components/mobile/ui/ConfirmarHoja';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { Hoja } from '#components/mobile/ui/Hoja';
import { HojaAcciones } from '#components/mobile/ui/HojaAcciones';
import { HuecoEmoji } from '#components/mobile/ui/HuecoEmoji';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { conSimboloEuro } from '#components/mobile/ui/Importe';
import { Pildora } from '#components/mobile/ui/Pildora';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import { SelectorMes } from '#components/mobile/ui/SelectorMes';
import { zonaTactil } from '#components/mobile/ui/tactil';
import {
  color,
  colorCategoria,
  densidad,
  espacio,
  movimiento,
  num,
  radio,
  sombra,
  texto,
} from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useLocalPref } from '#hooks/useLocalPref';
import { useNavigate } from '#hooks/useNavigate';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { useSpreadsheet } from '#hooks/useSpreadsheet';
import { useUndo } from '#hooks/useUndo';
import { pushModal } from '#modals/modalsSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { aqlQuery } from '#queries/aqlQuery';
import { useDispatch } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';

import {
  desgloseFaltante,
  importeConObjetivo,
  opcionDeshabilitada,
  planAsignarFaltante,
} from './asignarFaltante';
import { describe as describeExpression } from './assignExpression';
import { AssignKeypad } from './AssignKeypad';
import { AssignKeypadProvider, useAssignKeypad } from './AssignKeypadContext';
import type { ModoBorradorTeclado } from './AssignKeypadContext';
import {
  accionGuardar,
  asignarEnBorrador,
  asignarVariosEnBorrador,
  BORRADOR_VACIO,
  CLAVE_PESTANA,
  claveBorrador,
  contarCambios,
  filtrarPorPestana,
  leerBorrador,
  leerPestana,
  listosConBorrador,
  mapaConBorrador,
  marcarSuficiente,
} from './borrador';
import type { Borrador, CambioBorrador, PestanaAsignar } from './borrador';
import { BotonesFila } from './BotonesFila';
import {
  asignarGastadoMesPasado,
  estadoFila,
  idNotaMes,
  mediaPorCategoria,
  notaConIgnorarMes,
  reducirSobrefinanciacion,
  sobrefinanciado,
} from './objetivos';
import type {
  DatosCategoriaMes,
  EstadoFila,
  ImporteCategoria,
} from './objetivos';
import { RowName } from './RowName';
import { useDatosObjetivos } from './useDatosObjetivos';
import { useFichaCategoria } from './useFichaCategoria';
import { usePagadas } from './usePagadas';

type OnBudgetAction = (month: string, type: string, args?: unknown) => void;

/** Adónde se iba al tocar Volver o cambiar de mes con el borrador a medias. */
type Salida = { tipo: 'volver' } | { tipo: 'mes'; mes: string };

function leerSesion(clave: string): string | null {
  try {
    return window.sessionStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribirSesion(clave: string, valor: string | null) {
  try {
    if (valor == null) {
      window.sessionStorage.removeItem(clave);
    } else {
      window.sessionStorage.setItem(clave, valor);
    }
  } catch {
    // Sin sessionStorage (modo privado): el borrador vive solo en memoria.
  }
}

/**
 * Borrador del mes (6-oct): en memoria y en sessionStorage, para que un
 * refresco no lo pierda. Cada mes tiene el suyo.
 */
function useBorradorMes(month: string) {
  const [estado, setEstado] = useState<{ month: string; borrador: Borrador }>(
    () => ({ month, borrador: leerBorrador(leerSesion(claveBorrador(month))) }),
  );
  const borrador =
    estado.month === month
      ? estado.borrador
      : leerBorrador(leerSesion(claveBorrador(month)));
  const setBorrador = useCallback(
    (cambiar: (anterior: Borrador) => Borrador) => {
      setEstado(actual => {
        const base =
          actual.month === month
            ? actual.borrador
            : leerBorrador(leerSesion(claveBorrador(month)));
        const nuevo = cambiar(base);
        escribirSesion(
          claveBorrador(month),
          Object.keys(nuevo).length > 0 ? JSON.stringify(nuevo) : null,
        );
        return { month, borrador: nuevo };
      });
    },
    [month],
  );
  return { borrador, setBorrador };
}

/**
 * «Asignar el mes» (solo móvil), calcada de «Assign Money» de YNAB: cuánto
 * hay listo para asignar, cuánto falta en las categorías con objetivo,
 * auto-asignar y la lista de categorías con su barra de progreso. Tocar una
 * fila abre el mismo teclado que el Plan (botón «Asignar lo que falta»).
 */
export function AsignarMesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const spreadsheet = useSpreadsheet();
  const [searchParams, setSearchParams] = useSearchParams();

  const currMonth = monthUtils.currentMonth();
  const monthParam = searchParams.get('month');
  const month =
    monthParam && monthUtils.isValidYearMonth(monthParam)
      ? monthParam
      : currMonth;

  const [monthBounds, setMonthBounds] = useState({
    start: month,
    end: month,
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      const { start, end } = await send('get-budget-bounds');
      await prewarmMonth(spreadsheet, month);
      // El objetivo de cada categoría se recalcula para este mes sin tocar
      // lo asignado: así «infrafinanciado» está al día aunque no se hayan
      // aplicado las plantillas.
      try {
        await send('budget/refresh-goals', { month });
      } catch {
        // Sin plantillas no hay nada que refrescar.
      }
      if (!cancelled) {
        setMonthBounds({ start, end });
        setInitialized(true);
      }
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, [month, spreadsheet]);

  const applyBudgetAction = useBudgetActions();
  const onBudgetAction = useCallback<OnBudgetAction>(
    (month, type, args) => {
      if (!comprobarPlan()) {
        return;
      }
      applyBudgetAction.mutate({
        month,
        type,
        args,
      } as ApplyBudgetActionPayload);
    },
    [applyBudgetAction],
  );

  const setMonth = useCallback(
    (newMonth: string) => {
      setSearchParams({ month: newMonth }, { replace: true });
    },
    [setSearchParams],
  );

  const navigate = useNavigate();

  // Borrador: lo asignado aquí no se guarda hasta «Guardar presupuesto».
  const { borrador, setBorrador } = useBorradorMes(month);
  const hayBorrador = Object.keys(borrador).length > 0;
  // Los importes guardados de cada categoría (los rellena `Contenido`), para
  // saber si un importe del teclado es un cambio de verdad.
  const datosRef = useRef<ReadonlyMap<string, DatosCategoriaMes>>(new Map());
  const modoBorrador = useMemo<ModoBorradorTeclado>(
    () => ({
      cambioDe: id => borrador[id],
      asignar: (category, amount) =>
        setBorrador(b =>
          asignarEnBorrador(
            b,
            category.id,
            amount,
            datosRef.current.get(category.id)?.budgeted ?? 0,
          ),
        ),
      marcarSuficiente: (category, suficiente) =>
        setBorrador(b => marcarSuficiente(b, category.id, suficiente)),
    }),
    [borrador, setBorrador],
  );

  const preguntasAyuda = usePreguntasAsignar();
  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  const [salida, setSalida] = useState<Salida | null>(null);
  const salir = useCallback(
    (destino: Salida) => {
      setSalida(null);
      if (destino.tipo === 'volver') {
        void navigate(-1);
      } else {
        setMonth(destino.mes);
      }
    },
    [navigate, setMonth],
  );
  const intentarSalir = useCallback(
    (destino: Salida) => {
      if (hayBorrador) {
        setSalida(destino);
      } else {
        salir(destino);
      }
    },
    [hayBorrador, salir],
  );

  const onOpenSummary = useCallback(() => {
    dispatch(
      pushModal({
        modal: {
          name: 'envelope-budget-summary',
          options: {
            month,
            onBudgetAction: async (m, type, args) =>
              onBudgetAction(m, type, args),
          },
        },
      }),
    );
  }, [dispatch, month, onBudgetAction]);

  // Brújula: el resumen del mes (antes, el botón ⓘ); «Cambiar de mes» lo
  // declara el selector y «Filtros», la lista.
  useOpcionesPantalla([
    {
      id: 'resumen',
      tipo: 'propia',
      texto: t('Month summary'),
      icono: 'info',
      onPress: onOpenSummary,
    },
  ]);

  return (
    <Page
      padding={0}
      header={
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: espacio.fila,
            padding: `6px ${espacio.margen}px`,
            minHeight: 56,
          }}
        >
          <BotonRedondo
            icono="cl"
            aria-label={t('Back')}
            onPress={() => intentarSalir({ tipo: 'volver' })}
            data-testid="asignar-volver"
          />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              role="heading"
              aria-level={1}
              style={{ ...texto.titulo, color: color.fg, whiteSpace: 'nowrap' }}
            >
              <Trans>Assign the month</Trans>
            </Text>
            <SelectorMes
              mes={month}
              minimo={monthBounds.start}
              maximo={monthUtils.subMonths(monthBounds.end, 1)}
              onChange={mes => intentarSalir({ tipo: 'mes', mes })}
              enBrujula
              style={{ marginLeft: -6, minHeight: 32, alignSelf: 'flex-start' }}
            />
          </View>
          {/* Sin datos aún, la ayuda no tendría nada que enseñar. */}
          {initialized && <OjoBuey onPress={() => setAyudaAbierta(true)} />}
        </View>
      }
    >
      {initialized ? (
        <SheetNameProvider name={monthUtils.sheetForMonth(month)}>
          <AssignKeypadProvider
            month={month}
            onBudgetAction={onBudgetAction}
            borrador={modoBorrador}
          >
            <Contenido
              month={month}
              onBudgetAction={onBudgetAction}
              borrador={borrador}
              setBorrador={setBorrador}
              datosRef={datosRef}
              salida={salida}
              onSeguir={() => setSalida(null)}
              onSalir={salir}
            />
          </AssignKeypadProvider>
        </SheetNameProvider>
      ) : (
        <Cargando pantalla />
      )}
      {ayudaAbierta && (
        <AyudaGavi
          pantalla={t('Assign the month')}
          preguntas={preguntasAyuda}
          onCerrar={() => setAyudaAbierta(false)}
        />
      )}
    </Page>
  );
}

type GrupoVisible = CategoryGroupEntity & { categories: CategoryEntity[] };

type ContenidoProps = {
  month: string;
  onBudgetAction: OnBudgetAction;
  borrador: Borrador;
  setBorrador: (cambiar: (anterior: Borrador) => Borrador) => void;
  datosRef: { current: ReadonlyMap<string, DatosCategoriaMes> };
  salida: Salida | null;
  onSeguir: () => void;
  onSalir: (destino: Salida) => void;
};

/** Pestañas de la lista (6-oct): sustituyen al chip «Filtros» del Plan. */
function usePestana() {
  const [pestana, setPestanaEstado] = useState<PestanaAsignar>(() => {
    try {
      return leerPestana(window.localStorage.getItem(CLAVE_PESTANA));
    } catch {
      return 'todas';
    }
  });
  const setPestana = useCallback((valor: PestanaAsignar) => {
    setPestanaEstado(valor);
    try {
      window.localStorage.setItem(CLAVE_PESTANA, valor);
    } catch {
      // Sin localStorage: se recuerda solo mientras dure la pantalla.
    }
  }, []);
  return [pestana, setPestana] as const;
}

/**
 * Categorías marcadas como «Pago fijo» en su ficha (pref `pagos-fijos`):
 * pestaña «Pagos fijos». La misma marca que usan el Plan y «Pagos fijos».
 */
function useCategoriasPagoFijo(month: string): ReadonlySet<string> {
  return usePagadas(month).fijos;
}

function Contenido({
  month,
  onBudgetAction,
  borrador,
  setBorrador,
  datosRef,
  salida,
  onSeguir,
  onSalir,
}: ContenidoProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { showUndoNotification } = useUndo();
  const keypad = useAssignKeypad();
  // La ayuda de Gavi enseña la barra de guardar aunque no haya cambios.
  const fantasmaGuardar = useSyncExternalStore(
    suscribirFantasmaGuardar,
    hayFantasmaGuardar,
  );
  const {
    data: { grouped: categoryGroups } = {
      grouped: [] as CategoryGroupEntity[],
    },
  } = useCategories();

  // Grupos de gasto visibles con sus categorías visibles.
  const grupos = useMemo<GrupoVisible[]>(
    () =>
      categoryGroups
        .filter(g => !g.is_income && !g.hidden)
        .map(g => ({
          ...g,
          categories: (g.categories ?? []).filter(c => !c.hidden),
        }))
        .filter(g => g.categories.length > 0),
    [categoryGroups],
  );
  const categorias = useMemo(() => grupos.flatMap(g => g.categories), [grupos]);

  const datosHoja = useDatosObjetivos(month, categorias);
  datosRef.current = datosHoja;
  const toBudgetHoja =
    useSheetValue<'envelope-budget', typeof envelopeBudget.toBudget>(
      envelopeBudget.toBudget,
    ) ?? 0;
  // Todo lo de la pantalla se ve como quedaría al guardar el borrador.
  const datos = useMemo(
    () => mapaConBorrador(datosHoja, borrador),
    [datosHoja, borrador],
  );
  const toBudget = listosConBorrador(toBudgetHoja, borrador, datosHoja);
  const numCambios = contarCambios(borrador, datosHoja);
  const nombreMes = monthUtils.format(month, 'MMMM', locale);

  const asignarEnFila = useCallback(
    (category: CategoryEntity, amount: IntegerAmount) =>
      setBorrador(b =>
        asignarEnBorrador(
          b,
          category.id,
          amount,
          datosHoja.get(category.id)?.budgeted ?? 0,
        ),
      ),
    [datosHoja, setBorrador],
  );
  const aplicarVarios = useCallback(
    (amounts: ImporteCategoria[]) =>
      setBorrador(b => asignarVariosEnBorrador(b, amounts, datosHoja)),
    [datosHoja, setBorrador],
  );

  // «Guardar presupuesto»: una acción `budget-amounts` con todos los
  // importes y la marca de pausa del mes en las «Suficiente». Un aviso con
  // «Deshacer» que lo deja todo como estaba.
  const [guardando, setGuardando] = useState(false);
  const guardar = useCallback(async () => {
    const accion = accionGuardar(borrador, datosHoja);
    keypad?.cancel();
    setGuardando(true);
    try {
      if (accion.amounts.length > 0) {
        onBudgetAction(month, 'budget-amounts', { amounts: accion.amounts });
      }
      const notasAntes = new Map<string, string>();
      if (accion.suficientes.length > 0) {
        const ids = accion.suficientes.map(id => idNotaMes(id, month));
        const { data } = await aqlQuery(
          q('notes')
            .filter({ id: { $oneof: ids } })
            .select('*'),
        );
        for (const nota of (data ?? []) as NoteEntity[]) {
          notasAntes.set(nota.id, nota.note ?? '');
        }
        for (const id of ids) {
          await send('notes-save', {
            id,
            note: notaConIgnorarMes(notasAntes.get(id), true),
          });
        }
      }
      setBorrador(() => BORRADOR_VACIO);
      showUndoNotification({
        message: t('{{month}} budget saved', { month: nombreMes }),
        button: {
          title: t('Undo'),
          action: async () => {
            if (accion.anteriores.length > 0) {
              onBudgetAction(month, 'budget-amounts', {
                amounts: accion.anteriores,
              });
            }
            for (const id of accion.suficientes) {
              const idNota = idNotaMes(id, month);
              await send('notes-save', {
                id: idNota,
                note: notasAntes.get(idNota) ?? '',
              });
            }
          },
        },
      });
    } finally {
      setGuardando(false);
    }
  }, [
    borrador,
    datosHoja,
    keypad,
    month,
    nombreMes,
    onBudgetAction,
    setBorrador,
    showUndoNotification,
    t,
  ]);
  const descartar = useCallback(() => {
    keypad?.cancel();
    setBorrador(() => BORRADOR_VACIO);
  }, [keypad, setBorrador]);
  const [confirmandoDescartar, setConfirmandoDescartar] = useState(false);
  // B4 · H39: lo que falta = objetivos + gastado de más (sin contar dos
  // veces). «Incluir lo gastado de más» lo comparten píldora y auto-asignar.
  const desglose = desgloseFaltante(datos.values());
  const [incluirGastado, setIncluirGastado] = useState(true);

  // Pestañas propias de esta pantalla (no el chip «Filtros» del Plan).
  const [pestana, setPestanaElegida] = usePestana();
  const conPagoFijo = useCategoriasPagoFijo(month);
  const setPestana = (valor: PestanaAsignar) => {
    keypad?.cancel();
    setPestanaElegida(valor);
  };
  const gruposFiltrados = useMemo(
    () => filtrarPorPestana(grupos, pestana, datosHoja, conPagoFijo),
    [grupos, pestana, datosHoja, conPagoFijo],
  );
  const hayFiltro = pestana !== 'todas';
  const [collapsedGroupIds = [], setCollapsedGroupIdsPref] =
    useLocalPref('budget.collapsed');
  const onToggleCollapse = useCallback(
    (id: string) => {
      setCollapsedGroupIdsPref(
        collapsedGroupIds.includes(id)
          ? collapsedGroupIds.filter(c => c !== id)
          : [...collapsedGroupIds, id],
      );
    },
    [collapsedGroupIds, setCollapsedGroupIdsPref],
  );

  const abrirFicha = useFichaCategoria(month);

  const panelHeight = keypad?.panelHeight ?? 0;

  return (
    <View
      style={{
        // flexShrink 0: si la lista se encoge al alto del contenedor con
        // scroll, el relleno inferior queda dentro y no deja subir la última
        // fila por encima del teclado.
        flexShrink: 0,
        paddingBottom: Math.max(
          MOBILE_NAV_HEIGHT + (numCambios > 0 ? ALTO_BARRA_GUARDAR : 0),
          panelHeight + 10,
        ),
        gap: 0,
      }}
      data-testid="asignar-mes"
    >
      <Cabecera
        toBudget={toBudget}
        desglose={desglose}
        conBorrador={numCambios > 0}
      />
      <AutoAsignar
        month={month}
        grupos={grupos}
        datos={datos}
        toBudget={toBudget}
        onAplicar={aplicarVarios}
        gastadoDeMas={desglose.gastado}
        incluirGastado={incluirGastado}
        onIncluirGastado={setIncluirGastado}
      />
      <View
        data-testid="fila-filtros"
        style={{
          flexShrink: 0,
          flexDirection: 'row',
          padding: `4px ${densidad.margen}px 0`,
        }}
      >
        <Segmentado<PestanaAsignar>
          etiqueta={t('Show categories')}
          opciones={[
            { valor: 'con-objetivo', texto: t('With target') },
            { valor: 'sin-objetivo', texto: t('No target') },
            { valor: 'pagos-fijos', texto: t('Fixed payments') },
            { valor: 'todas', texto: t('All') },
          ]}
          valor={pestana}
          onChange={setPestana}
          testId="asignar-pestana"
          estirar
          style={{ flex: 1 }}
        />
      </View>
      <View
        aria-hidden
        data-testid="cabecera-columnas"
        style={{
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          padding: `10px ${densidad.margen}px 6px`,
          ...texto.etiqueta,
          fontWeight: 700,
          color: color.fg3,
        }}
      >
        <Text style={{ flex: 1, minWidth: 0 }}>
          <Trans>Category</Trans>
        </Text>
        <Text>
          <Trans>Budgeted</Trans>
        </Text>
      </View>
      <>
        {gruposFiltrados.map(grupo => {
          const plegado = collapsedGroupIds.includes(grupo.id);
          const asignadoGrupo = grupo.categories.reduce(
            (suma, c) => suma + (datos.get(c.id)?.budgeted ?? 0),
            0,
          );
          return (
            <View
              key={grupo.id}
              data-testid="asignar-grupo"
              style={{
                flexShrink: 0,
                borderBottom: `1px solid ${color.line}`,
              }}
            >
              <CabeceraGrupo
                grupo={grupo}
                asignado={asignadoGrupo}
                plegado={plegado}
                onToggle={() => onToggleCollapse(grupo.id)}
              />
              {!plegado &&
                grupo.categories.map((category, i) => (
                  <FilaCategoria
                    primera={i === 0}
                    key={category.id}
                    category={category}
                    month={month}
                    datos={datos.get(category.id)}
                    original={datosHoja.get(category.id)}
                    cambio={borrador[category.id]}
                    onAsignar={asignarEnFila}
                  />
                ))}
            </View>
          );
        })}
      </>
      {grupos.length === 0 && (
        <EstadoVacio
          style={{ margin: espacio.margen }}
          titulo={<Trans>No categories to assign.</Trans>}
        />
      )}
      {grupos.length > 0 && hayFiltro && gruposFiltrados.length === 0 && (
        <EstadoVacio
          style={{ margin: espacio.margen }}
          titulo={<Trans>No categories in this filter.</Trans>}
        />
      )}
      {numCambios > 0 && !keypad?.selectedCategory && (
        <BarraGuardar
          cambios={numCambios}
          guardando={guardando}
          onGuardar={() => void guardar()}
          onDescartar={() => setConfirmandoDescartar(true)}
        />
      )}
      {fantasmaGuardar && numCambios === 0 && !keypad?.selectedCategory && (
        <BarraGuardar cambios={2} fantasma />
      )}
      {confirmandoDescartar && (
        <Hoja
          aria-label={t('Discard changes')}
          onCerrar={() => setConfirmandoDescartar(false)}
        >
          <ConfirmarHoja
            icono="trash"
            pregunta={t('Discard the changes for {{month}}?', {
              month: nombreMes,
            })}
            detalle={t('{{count}} unsaved changes will be lost.', {
              count: numCambios,
            })}
            textoAccion={t('Discard')}
            testIdAccion="asignar-descartar-confirmar"
            onConfirmar={() => {
              setConfirmandoDescartar(false);
              descartar();
            }}
            onCancelar={() => setConfirmandoDescartar(false)}
          />
        </Hoja>
      )}
      {salida && (
        <HojaSalir
          mes={nombreMes}
          cambios={numCambios}
          onGuardar={async () => {
            await guardar();
            onSalir(salida);
          }}
          onDescartar={() => {
            descartar();
            onSalir(salida);
          }}
          onSeguir={onSeguir}
        />
      )}
      <AssignKeypad onEditCategory={abrirFicha} />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Cabecera verde y auto-asignar
// ---------------------------------------------------------------------------

function Cabecera({
  toBudget,
  desglose,
  conBorrador = false,
}: {
  toBudget: IntegerAmount;
  desglose: ReturnType<typeof desgloseFaltante>;
  /** Hay cambios sin guardar: la cifra ya los cuenta. */
  conBorrador?: boolean;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const sobreasignado = toBudget < 0;
  const [verDesglose, setVerDesglose] = useState(false);
  return (
    <View
      data-testid="asignar-cabecera"
      data-signo={
        toBudget < 0 ? 'negativo' : toBudget > 0 ? 'positivo' : 'cero'
      }
      style={{
        flexShrink: 0,
        margin: `4px ${espacio.margen}px ${espacio.tarjetas}px`,
        padding: '18px 12px 20px',
        borderRadius: radio.heroe,
        alignItems: 'center',
        gap: 4,
        textAlign: 'center',
        boxShadow: sombra.tarjeta,
        background: sobreasignado
          ? `linear-gradient(150deg, ${color.bad}, color-mix(in srgb, ${color.bad} 70%, black))`
          : `linear-gradient(150deg, ${color.heroA}, ${color.heroB})`,
        color: sobreasignado ? '#fff' : color.heroFg,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: 'stretch',
          gap: 8,
        }}
      >
        <PrivacyFilter>
          <Text
            style={{
              ...num,
              ...texto.heroe,
              color: 'inherit',
              whiteSpace: 'nowrap',
            }}
            data-testid="asignar-listo"
          >
            {format(Math.abs(toBudget), 'financial')}
          </Text>
        </PrivacyFilter>
      </View>
      <Text style={{ fontWeight: 700, opacity: 0.92, color: 'inherit' }}>
        {sobreasignado ? t('Overbudgeted') : t('ready to assign')}
      </Text>
      {conBorrador && (
        <Text
          data-testid="asignar-listo-borrador"
          style={{ fontSize: 12.5, fontWeight: 600, opacity: 0.9 }}
        >
          {sobreasignado
            ? t('Not enough money for these changes')
            : t('Counting unsaved changes')}
        </Text>
      )}
      {/* B4 · H39: «Faltan 818 € · Asignar», oculta si no falta nada; al
          tocarla, el desglose objetivos · gastado de más. */}
      {desglose.total > 0 && (
        <Button
          variant="bare"
          onPress={() => setVerDesglose(v => !v)}
          aria-expanded={verDesglose}
          data-testid="asignar-infrafinanciado"
          style={{
            ...num,
            marginTop: 6,
            minHeight: 36,
            fontSize: 13.5,
            fontWeight: 700,
            color: 'inherit',
            backgroundColor: 'rgba(0, 0, 0, 0.18)',
            borderRadius: radio.pildora,
            padding: '0 14px',
          }}
        >
          <PrivacyFilter>
            {t('{{amount}} missing · Assign', {
              amount: format(desglose.total, 'financial'),
            })}
          </PrivacyFilter>
        </Button>
      )}
      {desglose.total > 0 && verDesglose && (
        <PrivacyFilter>
          <Text
            data-testid="asignar-desglose"
            style={{
              ...num,
              fontSize: 13,
              fontWeight: 600,
              opacity: 0.92,
              color: 'inherit',
              marginTop: 2,
            }}
          >
            {t('{{targets}} targets · {{overspent}} overspent', {
              targets: format(desglose.objetivos, 'financial'),
              overspent: format(desglose.gastado, 'financial'),
            })}
          </Text>
        </PrivacyFilter>
      )}
    </View>
  );
}

type AutoAsignarProps = {
  month: string;
  grupos: GrupoVisible[];
  datos: Map<string, DatosCategoriaMes>;
  toBudget: IntegerAmount;
  /** Pone los importes en el borrador (no en el presupuesto). */
  onAplicar: (amounts: ImporteCategoria[]) => void;
  gastadoDeMas: IntegerAmount;
  incluirGastado: boolean;
  onIncluirGastado: (incluir: boolean) => void;
};

/** Valores `<campo>-<id>` de la hoja de un mes (`envelope-budget-month`). */
async function leerMes(
  month: string,
  campo: 'budget' | 'sum-amount',
): Promise<Map<string, number | null>> {
  const valores = await send('envelope-budget-month', { month });
  const mapa = new Map<string, number | null>();
  const marca = `!${campo}-`;
  for (const { name, value } of valores) {
    const i = name.indexOf(marca);
    if (i >= 0) {
      mapa.set(
        name.slice(i + marca.length),
        typeof value === 'number' ? value : null,
      );
    }
  }
  return mapa;
}

/** Historia de los 3 meses anteriores: asignado y gastado (positivo). */
type Historia = {
  asignado: Map<string, number | null>[];
  gastado: Map<string, number | null>[];
};

const MESES_PROMEDIO = 3;

function useHistoria(month: string, activo: boolean): Historia | null {
  const [historia, setHistoria] = useState<{
    month: string;
    datos: Historia;
  } | null>(null);
  useEffect(() => {
    if (!activo) {
      return;
    }
    let cancelado = false;
    void (async () => {
      const meses = Array.from({ length: MESES_PROMEDIO }, (_, i) =>
        monthUtils.subMonths(month, i + 1),
      );
      try {
        const asignado = await Promise.all(
          meses.map(m => leerMes(m, 'budget')),
        );
        const gastado = (
          await Promise.all(meses.map(m => leerMes(m, 'sum-amount')))
        ).map(
          m =>
            new Map(
              [...m].map(([id, v]) => [id, v == null ? null : Math.max(0, -v)]),
            ),
        );
        if (!cancelado) {
          setHistoria({ month, datos: { asignado, gastado } });
        }
      } catch {
        // Meses fuera del presupuesto: sin importes.
        if (!cancelado) {
          setHistoria({ month, datos: { asignado: [], gastado: [] } });
        }
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [activo, month]);
  return historia?.month === month ? historia.datos : null;
}

type OpcionAuto =
  | 'reduce-overfunding'
  | 'last-month'
  | 'spent-last'
  | 'avg-assigned'
  | 'avg-spent';

/** Icono de cada opción de «Más opciones…». */
const ICONO_OPCION: Record<OpcionAuto, NombreIcono> = {
  'reduce-overfunding': 'arrowdown',
  'last-month': 'cal',
  'spent-last': 'receipt',
  'avg-assigned': 'pie',
  'avg-spent': 'coins',
};

/**
 * B4 · H39/H40: botón principal «Asignar según objetivos (X)» que aplica
 * directamente (con Deshacer, sin hoja de vista previa), la casilla «Incluir
 * lo gastado de más» y una línea que dice qué va a pasar. El resto de
 * opciones, en «Más opciones»; las que piden más de lo listo para asignar
 * salen deshabilitadas.
 */
function AutoAsignar({
  month,
  grupos,
  datos,
  toBudget,
  onAplicar,
  gastadoDeMas,
  incluirGastado,
  onIncluirGastado,
}: AutoAsignarProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const dispatch = useDispatch();
  const keypad = useAssignKeypad();
  const [open, setOpen] = useState(false);
  const historia = useHistoria(month, open);

  const ids = useMemo(
    () => grupos.flatMap(g => g.categories.map(c => c.id)),
    [grupos],
  );
  const filas = useMemo(
    () =>
      ids.map(id => ({
        id,
        datos: datos.get(id) ?? {
          goal: null,
          longGoal: false,
          budgeted: 0,
          balance: 0,
          spent: 0,
        },
      })),
    [datos, ids],
  );
  const asignadoActual = useMemo(
    () => new Map(filas.map(f => [f.id, f.datos.budgeted])),
    [filas],
  );

  const plan = planAsignarFaltante(filas, toBudget, incluirGastado);

  // Importe de cada opción (lo que se asignaría en total), como YNAB.
  const propuestas = useMemo(() => {
    const porCategoria = (
      valores: Map<string, number | null> | Map<string, number> | undefined,
    ): ImporteCategoria[] | null =>
      valores
        ? ids.map(id => ({ category: id, amount: valores.get(id) ?? 0 }))
        : null;
    return {
      'reduce-overfunding': reducirSobrefinanciacion(filas),
      'last-month': porCategoria(historia?.asignado[0]),
      'spent-last': historia?.gastado[0]
        ? asignarGastadoMesPasado(
            ids.map(id => ({
              id,
              gastadoMesPasado: -(historia.gastado[0].get(id) ?? 0),
            })),
          )
        : null,
      'avg-assigned': historia
        ? porCategoria(mediaPorCategoria(ids, historia.asignado))
        : null,
      'avg-spent': historia
        ? porCategoria(mediaPorCategoria(ids, historia.gastado))
        : null,
    } satisfies Record<OpcionAuto, ImporteCategoria[] | null>;
  }, [filas, historia, ids]);

  const suma = (lista: ImporteCategoria[] | null) =>
    lista ? lista.reduce((s, a) => s + a.amount, 0) : null;
  const importes: Record<OpcionAuto, IntegerAmount | null> = {
    'reduce-overfunding': filas.reduce(
      (s, f) => s + sobrefinanciado(f.datos),
      0,
    ),
    'last-month': suma(propuestas['last-month']),
    'spent-last': suma(propuestas['spent-last']),
    'avg-assigned': suma(propuestas['avg-assigned']),
    'avg-spent': suma(propuestas['avg-spent']),
  };
  const conEuro = (valor: IntegerAmount | null) => {
    if (valor == null) {
      return '…';
    }
    const texto = format(valor, 'financial');
    return conSimboloEuro(texto);
  };

  const avisar = useCallback(
    (message: string) => {
      dispatch(
        addNotification({
          notification: { type: 'message', message, timeout: 5000 },
        }),
      );
    },
    [dispatch],
  );

  const aplicar = (amounts: ImporteCategoria[], message: string) => {
    // Lo tecleado en el panel deja de valer: la hoja pone los importes.
    keypad?.cancel();
    onAplicar(amounts);
    avisar(`${message} ${t('Not saved yet.')}`);
  };

  const onAsignarObjetivos = () => {
    if (plan.amounts.length === 0) {
      avisar(
        toBudget <= 0
          ? t('Nothing ready to assign.')
          : t('No underfunded categories.'),
      );
      return;
    }
    aplicar(
      plan.amounts,
      t('Assigned {{amount}} to {{count}} underfunded categories.', {
        amount: format(plan.total, 'financial'),
        count: plan.amounts.length,
      }),
    );
  };

  const onSelect = (name: OpcionAuto) => {
    setOpen(false);
    const amounts = propuestas[name];
    if (!amounts || amounts.length === 0) {
      avisar(
        name === 'reduce-overfunding'
          ? t('No overfunded categories.')
          : t('Nothing to assign from previous months.'),
      );
      return;
    }
    const mensajes: Record<OpcionAuto, string> = {
      'reduce-overfunding': t(
        'Returned {{amount}} from {{count}} overfunded categories.',
        {
          amount: format(importes['reduce-overfunding'] ?? 0, 'financial'),
          count: amounts.length,
        },
      ),
      'last-month': t("Budget set to last month's assigned amounts."),
      'spent-last': t("Budget set to last month's spending."),
      'avg-assigned': t('Budget set to the average assigned.'),
      'avg-spent': t('Budget set to the average spent.'),
    };
    aplicar(amounts, mensajes[name]);
  };

  const opciones: { name: OpcionAuto; text: string }[] = [
    { name: 'reduce-overfunding', text: t('Reduce overfunding') },
    { name: 'last-month', text: t('Assigned last month') },
    { name: 'spent-last', text: t('Spent last month') },
    { name: 'avg-assigned', text: t('Average assigned') },
    { name: 'avg-spent', text: t('Average spent') },
  ];

  // Qué va a pasar antes de aplicar.
  const lineaPlan =
    plan.necesario === 0
      ? t('No underfunded categories.')
      : plan.total === 0
        ? t('Nothing ready to assign.')
        : `${t('{{amount}} will be assigned to {{count}} categories.', {
            amount: format(plan.total, 'financial'),
            count: plan.amounts.length,
          })}${
            plan.limitado
              ? ` ${t('Only {{amount}} is ready to assign.', {
                  amount: format(Math.max(0, toBudget), 'financial'),
                })}`
              : ''
          }`;

  return (
    <View
      style={{
        flexShrink: 0,
        margin: `0 ${espacio.margen}px 8px`,
        gap: 8,
      }}
      data-testid="auto-asignar-bloque"
    >
      {gastadoDeMas > 0 && (
        <label
          data-testid="auto-asignar-incluir-gastado"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minHeight: 44,
            ...texto.secundario,
            color: color.fg,
            cursor: 'pointer',
          }}
        >
          <Checkbox
            checked={incluirGastado}
            onChange={() => onIncluirGastado(!incluirGastado)}
          />
          <Trans>Include overspending</Trans>
        </label>
      )}
      <PrivacyFilter>
        <Text
          data-testid="auto-asignar-plan"
          style={{ ...texto.secundario, color: color.fg2 }}
        >
          {lineaPlan}
        </Text>
      </PrivacyFilter>
      <Boton
        bloque
        onPress={onAsignarObjetivos}
        isDisabled={plan.amounts.length === 0}
        data-testid="auto-asignar"
      >
        <Icono nombre="zap" size={20} />
        <PrivacyFilter>
          {t('Assign by targets ({{amount}})', {
            amount: conEuro(plan.total),
          })}
        </PrivacyFilter>
      </Boton>
      <Boton
        variante="fantasma"
        onPress={() => setOpen(true)}
        data-testid="auto-asignar-mas"
      >
        <Trans>More options…</Trans>
      </Boton>
      {open && (
        <HojaAcciones
          titulo={t('Auto-assign to all categories')}
          subtitulo={
            <PrivacyFilter>
              {t('{{amount}} ready to assign', {
                amount: conEuro(Math.max(0, toBudget)),
              })}
            </PrivacyFilter>
          }
          onCerrar={() => setOpen(false)}
          data-testid="auto-asignar-opciones"
          filas={opciones.map(o => {
            const vacia = (propuestas[o.name]?.length ?? 0) === 0;
            const apagada = opcionDeshabilitada(
              propuestas[o.name],
              asignadoActual,
              toBudget,
            );
            return {
              id: o.name,
              texto: o.text,
              icono: ICONO_OPCION[o.name],
              valor: <PrivacyFilter>{conEuro(importes[o.name])}</PrivacyFilter>,
              deshabilitada: apagada,
              detalle: !apagada
                ? undefined
                : vacia
                  ? o.name === 'reduce-overfunding'
                    ? t('No overfunded categories.')
                    : t('Nothing to assign from previous months.')
                  : t('More than you have ready to assign'),
              onPress: () => onSelect(o.name),
            };
          })}
        />
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Grupos y filas
// ---------------------------------------------------------------------------

function CabeceraGrupo({
  grupo,
  asignado,
  plegado,
  onToggle,
}: {
  grupo: GrupoVisible;
  asignado: IntegerAmount;
  plegado: boolean;
  onToggle: () => void;
}) {
  const format = useFormat();
  return (
    <View
      data-testid="asignar-grupo-cabecera"
      style={{
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        height: densidad.altoGrupo,
        padding: `0 ${densidad.margen}px`,
        backgroundColor: color.surface2,
      }}
    >
      <RowName
        name={grupo.name}
        width="55vw"
        onPress={onToggle}
        lineas={1}
        minHeight={densidad.altoGrupo - 2}
        textStyle={{ ...densidad.grupo, color: color.fg }}
        data-testid="asignar-grupo-nombre"
        leading={
          <Icono
            nombre="cd"
            size={14}
            style={{
              flexShrink: 0,
              marginRight: 8,
              color: color.fg3,
              transition: `transform ${movimiento.pildora}ms ${movimiento.muelle}`,
              transform: plegado ? 'rotate(-90deg)' : '',
            }}
          />
        }
      />
      <PrivacyFilter>
        <Text
          style={{ ...densidad.grupoCifra, color: color.fg2, flexShrink: 0 }}
        >
          {format(asignado, 'financial')}
        </Text>
      </PrivacyFilter>
    </View>
  );
}

export function colorDeEstado(estado: EstadoFila): string {
  switch (estado.tipo) {
    case 'gastado-de-mas':
      return color.bad;
    case 'falta':
      return color.warn;
    case 'financiada':
      return color.ok;
    case 'sobrefinanciada':
      return colorCategoria(0);
    default:
      return color.fg3;
  }
}

/** Texto corto de estado de una fila, en castellano vía i18n. */
export function useTextoEstado() {
  const { t } = useTranslation();
  const format = useFormat();
  return useCallback(
    (estado: EstadoFila): string => {
      switch (estado.tipo) {
        case 'gastado-de-mas':
          return t('−{{amount}} overspent', {
            amount: format(estado.importe, 'financial'),
          });
        case 'falta':
          return t('{{amount}} needed', {
            amount: format(estado.importe, 'financial'),
          });
        case 'sobrefinanciada':
          return t('{{amount}} extra', {
            amount: format(estado.importe, 'financial'),
          });
        case 'financiada':
          return t('Funded');
        case 'ignorada':
          return t('Enough this month');
        default:
          return t('No target');
      }
    },
    [format, t],
  );
}

type FilaCategoriaProps = {
  category: CategoryEntity;
  month: string;
  /** Datos con el borrador aplicado. */
  datos: DatosCategoriaMes | undefined;
  /** Datos guardados (sin borrador), para «antes X». */
  original?: DatosCategoriaMes;
  cambio?: CambioBorrador;
  primera?: boolean;
  /** Pone un importe en el borrador. */
  onAsignar?: (category: CategoryEntity, amount: IntegerAmount) => void;
};

function FilaCategoria({
  category,
  month,
  datos,
  original,
  cambio,
  primera = false,
  onAsignar,
}: FilaCategoriaProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const keypad = useAssignKeypad();
  const textoEstado = useTextoEstado();
  const isSelected = keypad?.selectedCategory?.id === category.id;

  const estado = estadoFila(
    datos ?? { goal: null, longGoal: false, budgeted: 0, balance: 0, spent: 0 },
  );
  const colorEstado = colorDeEstado(estado);
  const { emoji, resto } = separarEmoji(category.name);

  // B4 · H41: «faltan 50,00 €» es una píldora que asigna lo que falta.
  const conObjetivo =
    estado.tipo === 'falta' && datos ? importeConObjetivo(datos) : null;
  const asignarFalta = () => {
    if (conObjetivo == null || !onAsignar) {
      return;
    }
    if (isSelected) {
      keypad?.cancel();
    }
    onAsignar(category, conObjetivo);
  };
  // Marca de «sin guardar»: el importe en color acento y «antes X» debajo
  // (o «sin guardar» si solo se marcó «Suficiente»).
  const antes = original?.budgeted ?? 0;
  const cambioAsignado = cambio?.asignado != null && cambio.asignado !== antes;
  const cambioSuficiente = !!cambio?.suficiente && !original?.ignorada;
  const sinGuardar = cambioAsignado || cambioSuficiente;

  // Mientras se teclea, la celda enseña la expresión («246,72 € +13,00 €»).
  const display =
    isSelected && keypad
      ? describeExpression(keypad.expression, keypad.liveBudgeted)
      : null;
  const asignado = display ? display.primary : (datos?.budgeted ?? 0);

  // La fila entera abre el teclado (botón de fondo); el ✓ de «Pagado este
  // mes» va encima y no lo abre.
  return (
    <View
      data-testid="asignar-fila"
      data-category-id={category.id}
      data-selected={isSelected || undefined}
      data-sin-guardar={sinGuardar || undefined}
      style={{
        position: 'relative',
        display: 'flex',
        flexShrink: 0,
        width: '100%',
        minHeight: densidad.altoFilaBarra,
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 4,
        padding: `8px ${densidad.margen}px 8px ${densidad.margen + densidad.sangria}px`,
        borderRadius: 0,
        boxShadow: primera ? undefined : `inset 0 1px 0 ${color.line}`,
        textAlign: 'left',
        color: color.fg,
        opacity: estado.tipo === 'ignorada' ? 0.62 : 1,
        backgroundColor: isSelected ? color.accentSoft : color.surface,
        transition: `background-color ${movimiento.pildora}ms`,
      }}
    >
      <Button
        variant="bare"
        onPress={() => keypad?.select(category)}
        aria-label={t('Assign to {{categoryName}}', {
          categoryName: category.name,
        })}
        data-testid="asignar-fila-boton"
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 0,
          backgroundColor: 'transparent',
        }}
      />
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        <View
          style={{
            flex: 1,
            minWidth: 0,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <HuecoEmoji emoji={emoji} />
          <Text
            style={{
              ...densidad.nombre,
              flex: 1,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: color.fg,
            }}
            data-testid="asignar-nombre"
          >
            {emoji ? resto : category.name}
          </Text>
          <BotonesFila category={category} month={month} />
        </View>
        <View
          data-testid="asignar-columna-cifra"
          style={{
            // B4 · H42: columna fija y cifra alineada a la derecha.
            alignItems: 'flex-end',
            width: densidad.colAsignado,
            flexShrink: 0,
            ...(isSelected && {
              outline: `2px solid ${color.accent}`,
              outlineOffset: 2,
              borderRadius: 6,
            }),
          }}
        >
          <PrivacyFilter>
            <Text
              style={{
                ...densidad.cifra,
                textAlign: 'right',
                whiteSpace: 'nowrap',
                color:
                  estado.tipo === 'gastado-de-mas' && !display
                    ? color.bad
                    : cambioAsignado && !display
                      ? color.accent
                      : color.fg,
              }}
              data-testid="asignar-asignado"
            >
              {format(asignado, 'financial')}
            </Text>
          </PrivacyFilter>
          {display?.secondary && (
            <PrivacyFilter>
              <Text
                style={{
                  ...densidad.pequeno,
                  ...num,
                  lineHeight: '14px',
                  color:
                    display.secondary.op === '-' ? color.bad : color.accent,
                }}
              >
                {display.secondary.op === '-' ? '−' : '+'}
                {format(display.secondary.amount, 'financial')}
              </Text>
            </PrivacyFilter>
          )}
          {!display?.secondary && sinGuardar && (
            <PrivacyFilter>
              <Text
                data-testid="asignar-antes"
                style={{
                  ...densidad.pequeno,
                  ...num,
                  lineHeight: '14px',
                  color: color.fg3,
                  whiteSpace: 'nowrap',
                }}
              >
                {cambioAsignado
                  ? t('before {{amount}}', {
                      amount: format(antes, 'financial'),
                    })
                  : t('not saved')}
              </Text>
            </PrivacyFilter>
          )}
        </View>
      </View>
      {/* Estado a la izquierda y barra a la derecha, bajo la cifra: una sola
          línea para que la fila quede en 56 px. */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          pointerEvents: 'none',
        }}
      >
        <View
          style={{
            flex: 1,
            minWidth: 0,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {estado.tipo === 'ignorada' && (
            <Icono
              nombre="check"
              size={14}
              strokeWidth={2.6}
              style={{ color: colorEstado }}
            />
          )}
          {conObjetivo != null && onAsignar ? (
            <Button
              variant="bare"
              onPress={asignarFalta}
              aria-label={t('Assign {{amount}} to {{categoryName}}', {
                amount: format(
                  conObjetivo - (datos?.budgeted ?? 0),
                  'financial',
                ),
                categoryName: category.name,
              })}
              data-testid="asignar-falta"
              className={css(zonaTactil(44))}
              style={{
                pointerEvents: 'auto',
                padding: 0,
                borderRadius: radio.pildora,
                backgroundColor: 'transparent',
                overflow: 'visible',
                minWidth: 0,
              }}
            >
              <PrivacyFilter>
                <Pildora
                  estado="aviso"
                  style={{ minHeight: 22, padding: '0 8px', fontSize: 12 }}
                  data-testid="asignar-falta-pildora"
                >
                  {textoEstado(estado)}
                </Pildora>
              </PrivacyFilter>
            </Button>
          ) : (
            <Text
              style={{
                ...densidad.pequeno,
                color: colorEstado,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
              data-testid="asignar-estado"
              data-estado={estado.tipo}
              data-ignorada={estado.tipo === 'ignorada' || undefined}
            >
              {textoEstado(estado)}
            </Text>
          )}
        </View>
        {/* B4 · H42: sin objetivo no hay barra (se guarda su hueco). */}
        {datos?.goal != null ? (
          <BarraProgreso
            valor={estado.progreso}
            color={colorEstado}
            alto={5}
            style={{ width: densidad.colAsignado - 20 }}
          />
        ) : (
          <View
            data-testid="asignar-sin-barra"
            style={{ width: densidad.colAsignado - 20, flexShrink: 0 }}
          />
        )}
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Borrador: barra «Guardar presupuesto» y hoja al salir
// ---------------------------------------------------------------------------

/** Alto de la barra de guardar (para el relleno inferior de la lista). */
const ALTO_BARRA_GUARDAR = 72;

type BarraGuardarProps = {
  cambios: number;
  guardando?: boolean;
  /** Dibujada por la ayuda de Gavi para enseñarla: no se puede pulsar. */
  fantasma?: boolean;
  onGuardar?: () => void;
  onDescartar?: () => void;
};

/**
 * Barra fija sobre la barra de pestañas mientras hay borrador: «Guardar
 * presupuesto · N cambios» y «Descartar» (zonas táctiles de 48 px).
 */
export function BarraGuardar({
  cambios,
  guardando = false,
  fantasma = false,
  onGuardar,
  onDescartar,
}: BarraGuardarProps) {
  const { t } = useTranslation();
  return (
    <View
      role={fantasma ? undefined : 'region'}
      aria-label={fantasma ? undefined : t('Unsaved changes')}
      aria-hidden={fantasma || undefined}
      inert={fantasma || undefined}
      data-testid="asignar-barra-guardar"
      data-fantasma={fantasma || undefined}
      style={{
        pointerEvents: fantasma ? 'none' : undefined,
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: `calc(${MOBILE_NAV_HEIGHT - 10}px + env(safe-area-inset-bottom, 0px))`,
        zIndex: 10,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: `10px ${espacio.margen}px`,
        backgroundColor: color.surface,
        borderTop: `1px solid ${color.line}`,
        boxShadow: sombra.tarjeta,
      }}
    >
      <Boton
        variante="fantasma"
        onPress={onDescartar}
        isDisabled={guardando}
        data-testid="asignar-descartar"
        style={{ minHeight: 48 }}
      >
        <Trans>Discard</Trans>
      </Boton>
      <Boton
        onPress={onGuardar}
        isDisabled={guardando}
        data-testid="asignar-guardar"
        style={{ flex: 1, minHeight: 48 }}
      >
        {t('Save budget · {{count}} changes', { count: cambios })}
      </Boton>
    </View>
  );
}

type HojaSalirProps = {
  mes: string;
  cambios: number;
  onGuardar: () => void | Promise<void>;
  onDescartar: () => void;
  onSeguir: () => void;
};

/** «¿Guardar los cambios de octubre?» al salir con el borrador a medias. */
function HojaSalir({
  mes,
  cambios,
  onGuardar,
  onDescartar,
  onSeguir,
}: HojaSalirProps) {
  const { t } = useTranslation();
  return (
    <Hoja
      aria-label={t('Save the changes for {{month}}?', { month: mes })}
      data-testid="asignar-hoja-salir"
      onCerrar={onSeguir}
    >
      <View style={{ gap: 8 }}>
        <Text
          role="heading"
          aria-level={2}
          style={{
            fontSize: 20,
            fontWeight: 800,
            textAlign: 'center',
            color: color.fg,
          }}
        >
          {t('Save the changes for {{month}}?', { month: mes })}
        </Text>
        <Text
          style={{
            ...texto.secundario,
            textAlign: 'center',
            color: color.fg2,
            marginBottom: 10,
          }}
        >
          {t('{{count}} unsaved changes.', { count: cambios })}
        </Text>
        <Boton
          bloque
          onPress={() => void onGuardar()}
          data-testid="asignar-salir-guardar"
        >
          <Trans>Save</Trans>
        </Boton>
        <Boton
          bloque
          variante="tonal"
          onPress={onDescartar}
          data-testid="asignar-salir-descartar"
        >
          <Trans>Discard</Trans>
        </Boton>
        <Boton
          bloque
          variante="fantasma"
          onPress={onSeguir}
          data-testid="asignar-salir-seguir"
        >
          <Trans>Stay here</Trans>
        </Boton>
      </View>
    </Hoja>
  );
}
